import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, endpoints } from '../api';
import { useSession } from '../session';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';
import { ErrorNote, FullPageMessage } from '../ui';

/**
 * Two steps on a fresh terminal: pair the device with the 8-character code
 * from Admin → Devices (once), then the cashier's 6-digit PIN (every shift).
 * Pairing sets the device cookie; the PIN sets the staff cookie
 * (docs/auth.md §3.1–3.2).
 */
export default function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'pin' | 'pair'>('pin');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: (value: string) => endpoints.pinLogin(value),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries();
      await navigate('/', { replace: true });
    },
    onError: (cause: ApiError) => {
      setPin('');
      if (cause.code === 'DEVICE_TOKEN_REQUIRED' || cause.code === 'DEVICE_NOT_RECOGNIZED') {
        setMode('pair');
        setError('This terminal is not paired yet. Enter its pairing code from Admin → Devices.');
      } else setError(cause.message);
    },
  });
  const pair = useMutation({
    mutationFn: (value: string) => endpoints.pair(value),
    onSuccess: () => {
      setError(null);
      setMode('pin');
      setCode('');
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (session.isPending) return <FullPageMessage title="Checking your session…" />;
  if (session.data) return <Navigate to="/" replace />;

  const press = (digit: string) => {
    if (mode !== 'pin' || pin.length >= 6) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 6) login.mutate(next);
  };

  return (
    <div className="grid min-h-full place-items-center bg-shell-900 p-6">
      <div className="w-full max-w-sm rounded-xl bg-surface p-8">
        <h1 className="text-lg font-semibold">{PLATFORM_NAME} Box Office</h1>
        <p className="mt-1 text-sm text-ink-400">{mode === 'pin' ? 'Enter your PIN' : 'Pair this terminal'}</p>
        {error ? (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        ) : null}
        {mode === 'pin' ? (
          <>
            <div className="mt-6 flex justify-center gap-3" aria-label="PIN entry">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-accent' : 'bg-line'}`} />
              ))}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={key === '' || login.isPending}
                  onClick={() => (key === '⌫' ? setPin(pin.slice(0, -1)) : press(key))}
                  className={`h-16 rounded-lg text-2xl font-semibold ${key === '' ? 'invisible' : 'bg-page hover:bg-line active:bg-accent-soft'}`}
                >
                  {key}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setMode('pair')} className="mt-6 text-xs text-ink-400 underline">
              Pair this terminal
            </button>
          </>
        ) : (
          <form
            className="mt-6 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              pair.mutate(code.trim().toUpperCase());
            }}
          >
            <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus placeholder="Pairing code" maxLength={8} className="touch w-full rounded-lg border border-line px-4 text-center text-xl tracking-[0.3em] uppercase" aria-label="Pairing code" />
            <button type="submit" disabled={code.trim().length < 8 || pair.isPending} className="touch w-full rounded-lg bg-accent font-semibold text-white disabled:opacity-40">
              Pair
            </button>
            <button type="button" onClick={() => setMode('pin')} className="text-xs text-ink-400 underline">
              Back to PIN
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
